import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchWithAuth } from '@/lib/api/client'

const API_URL = import.meta.env.VITE_API_URL!

interface UseWhisperTranscriptionOptions {
  onEnd?: (finalTranscript: string, hadInterimAtStop: string) => void
  onError?: (error: string, errorType: string) => void
}

export function useWhisperTranscription(options?: UseWhisperTranscriptionOptions) {
  // Check support synchronously - MediaRecorder is available in all modern browsers
  const hasMediaRecorder = typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined'

  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const onEndRef = useRef(options?.onEnd)
  const onErrorRef = useRef(options?.onError)

  // Keep callback refs up to date
  useEffect(() => {
    onEndRef.current = options?.onEnd
    onErrorRef.current = options?.onError
  }, [options?.onEnd, options?.onError])

  const transcribeAudio = async (audioBlob: Blob) => {
    try {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'audio.webm')

      const response = await fetchWithAuth(`${API_URL}/api/transcribe`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `Transcription failed: ${response.statusText}`)
      }

      const data = await response.json()
      return data.text
    } catch (error) {
      throw error
    }
  }

  const startListening = useCallback(async () => {
    if (isListening) return

    if (!hasMediaRecorder) {
      const errorMsg = 'Speech recognition is not supported in this browser.'
      setError(errorMsg)
      if (onErrorRef.current) {
        onErrorRef.current(errorMsg, 'not-supported')
      }
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      audioChunksRef.current = []

      // Try to use a compatible MIME type that Whisper supports
      let options: MediaRecorderOptions | undefined
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ]

      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          options = { mimeType }
          break
        }
      }

      const mediaRecorder = options
        ? new MediaRecorder(stream, options)
        : new MediaRecorder(stream)

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        // Stop all tracks first
        stream.getTracks().forEach(track => track.stop())

        // Create blob with appropriate MIME type
        const mimeType = audioChunksRef.current[0]?.type || 'audio/webm'
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })

        // Transcribe the audio
        try {
          const transcribedText = await transcribeAudio(audioBlob)
          setTranscript(transcribedText)

          if (onEndRef.current) {
            onEndRef.current(transcribedText, '')
          }
        } catch (error) {
          const errorMsg = 'Failed to transcribe audio. Please try again.'
          setError(errorMsg)
          if (onErrorRef.current) {
            onErrorRef.current(errorMsg, 'transcription-failed')
          }
        }

        setIsListening(false)
      }

      mediaRecorder.start()
      mediaRecorderRef.current = mediaRecorder
      setIsListening(true)
      setError(null)
    } catch (error) {
      const errorMsg = 'Microphone access denied. Please enable microphone permissions.'
      setError(errorMsg)
      if (onErrorRef.current) {
        onErrorRef.current(errorMsg, 'permission-denied')
      }
    }
  }, [isListening, hasMediaRecorder])

  const stopListening = useCallback(() => {
    if (mediaRecorderRef.current && isListening) {
      mediaRecorderRef.current.stop()
    }
  }, [isListening])

  const resetTranscript = useCallback(() => {
    setTranscript('')
  }, [])

  return {
    isListening,
    transcript,
    interimTranscript: '', // Whisper doesn't provide interim results
    isSupported: hasMediaRecorder,
    error,
    startListening,
    stopListening,
    resetTranscript,
  }
}
